//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Grid, Typography, Box } from '@mui/material'
import React from 'react'
import { EthAddr } from '@shared/SchemaTypes'
import {
  PoolAccountCTO,
  PoolBaseAccountCTO,
  PoolBaseAccountCTOSchema,
  PoolBaseOverviewCTO,
  PoolTermAccountCTOSchema,
} from '@shared/sharedTypes'
import ViewField from '@react/components/elements/ViewField'
import { SectionHeader } from '@react/components/common/SectionHeader'
import { REPORT_FETCH_DESCRIPTOR_TRANSACTION_LIST } from '@react/reporting/ReportDescriptors'
import ReportRowGenerator from '@react/reporting/ReportRowGenerator'

interface TermPoolHoldingsProps {
  poolAccountCTO: PoolAccountCTO
  poolBaseAccountCTO: PoolBaseAccountCTO
  poolBaseOverviewCTO: PoolBaseOverviewCTO
  poolAddr: EthAddr
  accountAddr: EthAddr
}

export default function TermPoolHoldings({
  poolAccountCTO,
  poolBaseAccountCTO,
  poolBaseOverviewCTO,
  poolAddr,
  accountAddr,
}: TermPoolHoldingsProps): React.ReactElement {
  return (
    <Box>
      <Box px={3} py={2} sx={{ flexGrow: 1 }}>
        <SectionHeader>
          <Typography variant={'captionBold'}>My Investment Details</Typography>
        </SectionHeader>

        <Grid container spacing={8} sx={{ padding: '24px', marginTop: '-24px', marginLeft: '0px' }}>
          <Grid item xs={12} sm={6}>
            <ViewField
              dataObject={poolBaseAccountCTO}
              poolTokenSymbol={poolBaseOverviewCTO.symbol}
              fieldSchema={PoolBaseAccountCTOSchema.describe().fields.tokenBalance}
              toolTip="The number of vault tokens you currently hold."
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <ViewField
              dataObject={poolAccountCTO}
              fieldSchema={PoolTermAccountCTOSchema.describe().fields.maxWithdrawRequest}
              poolTokenSymbol={poolBaseOverviewCTO.symbol}
              liquidityTokenSymbol={poolBaseOverviewCTO.liquidityTokenSymbol}
              toolTip="The maximum amount of USDC you can request to be withdrawn, either via early withdrawal request or rollover termination notice."
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <ViewField
              dataObject={poolBaseAccountCTO}
              poolTokenSymbol={poolBaseOverviewCTO.symbol}
              fieldSchema={PoolTermAccountCTOSchema.describe().fields.maxRedeemRequest}
              toolTip="The maximum number of vault tokens you can request to be redeemed. This applies for both Rollover Terminations and Early Withdrawal Requests."
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <ViewField
              dataObject={poolBaseAccountCTO}
              fieldSchema={PoolTermAccountCTOSchema.describe().fields.maxRedeem}
              poolTokenSymbol={poolBaseOverviewCTO.symbol}
              toolTip="After your withdrawal request or rollover termination has been processed and USDC is repaid into the Loan Vault, this is the maximum number of tokens you can redeem for USDC."
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <ViewField
              dataObject={poolBaseAccountCTO}
              fieldSchema={PoolTermAccountCTOSchema.describe().fields.maxWithdraw}
              liquidityTokenSymbol={poolBaseOverviewCTO.liquidityTokenSymbol}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <ViewField
              dataObject={poolBaseAccountCTO}
              fieldSchema={PoolTermAccountCTOSchema.describe().fields.principalEarningInterest}
              liquidityTokenSymbol={poolBaseOverviewCTO.liquidityTokenSymbol}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <ViewField
              dataObject={poolBaseAccountCTO}
              fieldSchema={PoolTermAccountCTOSchema.describe().fields.assetsTransferringIn}
              liquidityTokenSymbol={poolBaseOverviewCTO.liquidityTokenSymbol}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <ViewField
              dataObject={poolBaseAccountCTO}
              fieldSchema={PoolTermAccountCTOSchema.describe().fields.assetsTransferringOut}
              liquidityTokenSymbol={poolBaseOverviewCTO.liquidityTokenSymbol}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <ViewField
              dataObject={poolBaseAccountCTO}
              fieldSchema={PoolTermAccountCTOSchema.describe().fields.expectedInterestAtMaturity}
              liquidityTokenSymbol={poolBaseOverviewCTO.liquidityTokenSymbol}
            />
          </Grid>
        </Grid>

        <SectionHeader>
          <Typography variant={'captionBold'}>Vault Transactions</Typography>
        </SectionHeader>

        <Box sx={{ marginTop: 2 }}>
          <ReportRowGenerator
            reportDescriptor={REPORT_FETCH_DESCRIPTOR_TRANSACTION_LIST}
            initialLoadWithoutRefresh={true}
            poolAddr={poolAddr}
            lenderAddr={accountAddr}
            numberOfDays={30}
          />
        </Box>
      </Box>
    </Box>
  )
}
